// Ниши › Одит — importance per check (what counts as ❌ versus ℹ️ for this niche) and the
// wording the dossier uses for each check (`vertical_check_texts`).

import { useMemo, useState } from 'react';
import { CHECKS } from '@leadg/shared';
import {
  useCheckTexts,
  useSaveCheckTexts,
  type CheckTextRow,
  type SetVerticalField,
  type VerticalPatch,
} from './useVerticals.js';

const IMPORTANCE = [
  { key: 'required', label: 'задължително' },
  { key: 'optional', label: 'по избор' },
  { key: 'ignore', label: 'без значение' },
] as const;

const TEXT_FIELDS = [
  { key: 'title_bg', label: 'заглавие' },
  { key: 'missing_bg', label: 'когато липсва' },
  { key: 'improvement_bg', label: 'какво се прави' },
  { key: 'consequence_bg', label: 'какво струва' },
] as const;

type TextField = (typeof TEXT_FIELDS)[number]['key'];

function ImportanceGrid({ draft, set }: { draft: VerticalPatch; set: SetVerticalField }) {
  return (
    <div className="adm__importance">
      {CHECKS.map((check) => (
        <label key={check} className="adm__importance-row">
          <code className="adm__importance-key">{check}</code>
          <select
            className="adm__select"
            value={draft.check_importance[check] ?? 'optional'}
            onChange={(event) =>
              set('check_importance', { ...draft.check_importance, [check]: event.target.value })
            }
          >
            {IMPORTANCE.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
}

function emptyText(verticalKey: string, checkKey: string): CheckTextRow {
  return {
    id: null,
    vertical_key: verticalKey,
    check_key: checkKey,
    title_bg: '',
    ok_bg: null,
    missing_bg: null,
    improvement_bg: null,
    consequence_bg: null,
  };
}

function CheckTextsEditor({ verticalKey, saved }: { verticalKey: string; saved: CheckTextRow[] }) {
  const save = useSaveCheckTexts();
  const [edits, setEdits] = useState<Record<string, CheckTextRow>>({});

  const rows = useMemo(() => {
    const byKey = new Map(saved.map((row) => [row.check_key, row]));
    return CHECKS.map((check) => edits[check] ?? byKey.get(check) ?? emptyText(verticalKey, check));
  }, [saved, edits, verticalKey]);

  const edit = (row: CheckTextRow, field: TextField, value: string) =>
    setEdits((prev) => ({
      ...prev,
      [row.check_key]: { ...row, [field]: field === 'title_bg' ? value : value || null },
    }));

  const dirty = Object.values(edits);
  const withoutTitle = dirty.filter((row) => row.title_bg.trim() === '').length;

  return (
    <div className="adm__texts">
      <div className="adm__tablewrap">
        <table className="adm__table adm__table--texts">
          <thead>
            <tr>
              <th>проверка</th>
              {TEXT_FIELDS.map((field) => (
                <th key={field.key}>{field.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.check_key} className={edits[row.check_key] ? 'adm__row--dirty' : ''}>
                <td>
                  <code>{row.check_key}</code>
                </td>
                {TEXT_FIELDS.map((field) => (
                  <td key={field.key}>
                    <textarea
                      className="adm__textarea adm__textarea--cell"
                      rows={2}
                      value={row[field.key] ?? ''}
                      onChange={(event) => edit(row, field.key, event.target.value)}
                      aria-label={`${field.label}: ${row.check_key}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="adm__actions">
        <button
          type="button"
          className="adm__resolve-btn"
          disabled={dirty.length === 0 || withoutTitle > 0 || save.isPending}
          onClick={() => save.mutate(dirty, { onSuccess: () => setEdits({}) })}
        >
          {save.isPending ? 'Записва…' : `Запази текстовете (${dirty.length})`}
        </button>
        {withoutTitle > 0 ? (
          <span className="adm__err">Заглавието е задължително за всяка променена проверка.</span>
        ) : null}
        {save.isError ? <span className="adm__err">{(save.error as Error).message}</span> : null}
        {save.isSuccess && dirty.length === 0 ? <span className="adm__note">Записано.</span> : null}
      </div>
    </div>
  );
}

export function VerticalAuditTab({
  verticalKey,
  draft,
  set,
}: {
  verticalKey: string;
  draft: VerticalPatch;
  set: SetVerticalField;
}) {
  const { data: texts = [], isLoading } = useCheckTexts(verticalKey);

  return (
    <div className="adm__form">
      <fieldset className="adm__fieldset">
        <legend className="adm__legend">Тежест на проверките</legend>
        <p className="adm__note">
          Прекласифицира при следващото пускане на одита, без нови заявки към сайтовете.
        </p>
        <ImportanceGrid draft={draft} set={set} />
      </fieldset>
      <fieldset className="adm__fieldset">
        <legend className="adm__legend">Текстове в досието</legend>
        <p className="adm__note">
          Какво пише досието за всяка проверка при тази ниша. Празен ред означава неутралния текст
          по подразбиране с „{draft.audience_noun_bg || 'клиенти'}“.
        </p>
        {isLoading ? (
          <p className="adm__msg">Зареждане на текстовете…</p>
        ) : (
          <CheckTextsEditor key={verticalKey} verticalKey={verticalKey} saved={texts} />
        )}
      </fieldset>
    </div>
  );
}
